import { Checkbox } from '@mui/material'
import { Chevron } from '../ui-assets/chevronDown'
import React, { useEffect, useState } from 'react'
import LanguageStrings from '../i18n/locales'

const ComponentStrings = LanguageStrings().Components

export type CheckboxDropdownMode = 'all' | 'current' | 'none'

export interface CheckboxDropdownOption {
  label: string
  value: CheckboxDropdownMode
  trigger: () => void
}

export interface CheckboxDropdownProps {
  selectedOption: CheckboxDropdownMode
  setSelectedOption: React.Dispatch<React.SetStateAction<CheckboxDropdownMode>>
  onSelectionModeChange: (prevMode: CheckboxDropdownMode, newMode: CheckboxDropdownMode) => void
  currPageSelectedAssets: string[]
  currPageTotal: number
  containerRef: React.RefObject<HTMLDivElement>
  dataTestId?: string
  menuOptionDataTestIds?: string[]
  ariaLabel?: string
}

interface DropdownMenuProps {
  options: CheckboxDropdownOption[]
  selectedOption: CheckboxDropdownMode
  currPageSelectedAssets: string[]
  currPageTotal: number
  menuOptionDataTestIds?: string[]
  dataTestId?: string
}

const DropdownMenu = ({ options, selectedOption, currPageSelectedAssets, currPageTotal, menuOptionDataTestIds, dataTestId }: DropdownMenuProps): JSX.Element => {
  const dataTestIds: string[] = new Array(options.length).fill('')
  dataTestIds.forEach((_, idx) => {
    dataTestIds[idx] = menuOptionDataTestIds?.at(idx) ?? `${dataTestId ?? 'checkboxdropdown'}-${options.at(idx)?.value ?? 'menu'}-${idx}`
  })
  const optionStyles = (isFocused: boolean, isSelected: boolean): React.CSSProperties => {
    return {
      color: isSelected ? '#64C3FF' : '#f0f0f0',
      backgroundColor: (isSelected) ? '#1792E51A' : (isFocused) ? '#303030' : '#404040',
      width: 260,
      overflow: 'ellipses',
      display: 'flex',
      alignItems: 'center'
    }
  }
  const menuStyles = {
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    width: 260,
    backgroundColor: '#404040',
    fontSize: 16,
    fontWeight: 500,
    fontFamily: 'Honeywell Sans Web',
    cursor: 'pointer'
  }
  return (
    <div className="dropdown-menu" style={{ position: 'absolute', top: '100%', left: '0', zIndex: 1000 }}>
      {options.map((option, idx) => {
        function isSelected (optionValue: string): boolean {
          if (optionValue === 'all') {
            return selectedOption === 'all'
          } else {
            return selectedOption === 'current' && currPageTotal === currPageSelectedAssets.length
          }
        }
        return (
          <div
            key={`${option.value}-${idx}`}
            data-testid={dataTestIds.at(idx)}
            onClick={() => {
              option.trigger()
            }}
            style={{ ...menuStyles }}
          >
            <div style={ optionStyles(false, isSelected(option.value)) }>
              <Checkbox
                color="primary"
                sx={{ color: '#909090' }}
                data-testid={dataTestIds.at(idx)}
                checked={isSelected(option.value)}
                inputProps={{
                  'aria-label': `${dataTestIds.at(idx) ?? option.value}-label`,
                  id: 'select-all-items',
                  style: { padding: 0 }
                }}
              />
              <span>
                {option.label}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function CheckboxDropdown ({
  selectedOption,
  setSelectedOption,
  onSelectionModeChange,
  currPageSelectedAssets,
  currPageTotal,
  containerRef,
  dataTestId,
  menuOptionDataTestIds,
  ariaLabel
}: CheckboxDropdownProps): JSX.Element {
  // const [componentPosition, setComponentPosition] = useState<[number, number]>([0, 0])
  const [isOpen, setIsOpen] = useState(false)

  // Dropdown option triggers
  const dropdownOptions: CheckboxDropdownOption[] = [
    {
      label: ComponentStrings.SelectAll,
      value: 'all',
      trigger: () => {
        if (selectedOption !== 'all') {
          onSelectionModeChange(selectedOption, 'all')
          setSelectedOption('all')
          setIsOpen(false)
        } else {
          onSelectionModeChange(selectedOption, 'none')
          setSelectedOption('none')
          setIsOpen(false)
        }
      }
    },
    {
      label: ComponentStrings.SelectCurrentPage,
      value: 'current',
      trigger: () => {
        if (selectedOption !== 'current') {
          onSelectionModeChange(selectedOption, 'current')
          setSelectedOption('current')
          setIsOpen(false)
        } else {
          onSelectionModeChange(selectedOption, 'none')
          setSelectedOption('none')
          setIsOpen(false)
        }
      }
    }
  ]

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent): void => {
      if ((containerRef.current != null) && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div ref={containerRef} style={{ display: 'flex', alignItems: 'center', position: 'relative' }} data-testid={dataTestId ?? 'CheckboxDropdown'} onClick={() => { setIsOpen(!isOpen) }}>
      <Checkbox
        color="primary"
        sx={{ color: '#909090' }}
        data-testid="device-table-header-checkbox"
        checked={selectedOption === 'all' || (selectedOption === 'current' && currPageTotal === currPageSelectedAssets.length) || (selectedOption === 'none' && currPageTotal === currPageSelectedAssets.length)}
        inputProps={{
          'aria-label': ariaLabel ?? 'CheckboxDropdown checkbox',
          id: 'select-all-items',
          style: { padding: 0 }
        }}
      />
      <span style={{ cursor: 'pointer', display: 'flex' }}>
        <Chevron size={20} type={isOpen ? 'up' : 'down'} />
      </span>
      {isOpen && (
        <DropdownMenu
          options={dropdownOptions}
          selectedOption={selectedOption}
          currPageSelectedAssets={currPageSelectedAssets}
          currPageTotal={currPageTotal}
          menuOptionDataTestIds={menuOptionDataTestIds}
          dataTestId={dataTestId}
        />
      )}
    </div>
  )
}
